import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid, MenuItem, Typography } from '@mui/material';
import { common } from '@mui/material/colors';
import { BaseSelectField } from 'common/components/base-select-field';
import FormProvider from 'common/components/form-provider';
import { colors, styleSelect } from 'common/constants';
import { branchQuestionSchema } from 'common/schema';
import { FieldErrors } from 'react-hook-form';
import React, { useMemo, useState, useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { createStyleSheet, handleError } from 'utils/helpers';
import AddIcon from '@mui/icons-material/Add';
import { ReactComponent as PlusIcon } from 'assets/images/icons/question-design/plus-question.svg';
import { ReactComponent as SaveIcon } from 'assets/images/icons/question-design/save-question.svg';
import CloseIcon from '@mui/icons-material/Close';
import {
    IQuestionNewEditForm,
    IQuestionLogicBranchValues,
    IQuestionAnswer,
} from 'types/campaign-design';
import {
    ILogicOptions,
    IQuestionLogicOptions,
    IQuestionLogicValues,
} from 'types/campaign';
import { saveBranchLogicOptions } from 'services/campaign.service';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { listCampaignQuestionsFormatter } from 'utils/campaign_helpers';
import { useAppDispatch } from 'common/hook';
import { setIsLoading } from 'slices/loadingSlice';

const ConditionFieldArray: React.FC<{
    control: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    logicIndex: number;
    conditionOptions: ILogicOptions[];
    questionAnswers: IQuestionAnswer[];
    defaulLogic: IQuestionLogicBranchValues[];
}> = ({
    control,
    logicIndex,
    conditionOptions,
    questionAnswers,
    defaulLogic,
}) => {
    const { append: appendCondition, fields: conditionFields } = useFieldArray({
        control,
        name: `logics.${logicIndex}.conditions`,
    });
    return (
        <>
            {conditionFields.map((condition, conditionIndex) => (
                <Grid container key={condition.id} spacing={2} mb={1}>
                    <Grid item md={6}>
                        <BaseSelectField
                            name={`logics.${logicIndex}.conditions.${conditionIndex}.condition`}
                            sx={{
                                ...styleSelect,
                                '& .MuiSelect-icon': {
                                    right: '20px',
                                    fontSize: '50px ',
                                },
                            }}
                        >
                            {conditionOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </BaseSelectField>
                    </Grid>
                    <Grid item md={6}>
                        <BaseSelectField
                            name={`logics.${logicIndex}.conditions.${conditionIndex}.answer`}
                            sx={{
                                ...styleSelect,
                                '& .MuiSelect-icon': {
                                    right: '20px',
                                    fontSize: '50px ',
                                },
                            }}
                        >
                            {questionAnswers.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.answer}
                                </MenuItem>
                            ))}
                        </BaseSelectField>
                    </Grid>
                </Grid>
            ))}
            <Button
                variant="contained"
                sx={styles.btnAddLogic}
                disableRipple={false}
                startIcon={<AddIcon />}
                onClick={() => appendCondition(defaulLogic[0].conditions[0])}
            >
                Thêm logic
            </Button>
        </>
    );
};

interface LogicBranchQuestionProps {
    onClose: () => void;
    question: IQuestionNewEditForm;
    options: IQuestionLogicOptions;
    values: IQuestionLogicValues[];
    replaceQuestionByQuestionId: (
        questionId: number,
        question: IQuestionNewEditForm
    ) => void;
}

const LogicBranchQuestion: React.FC<LogicBranchQuestionProps> = ({
    onClose,
    question,
    options,
    values,
    replaceQuestionByQuestionId,
}) => {
    const conditionOptions = useMemo(
        () => options?.conditions || [],
        [options]
    );
    const questionOptions = useMemo(() => options?.questions || [], [options]);
    const questionAnswers = useMemo(() => {
        if (question.data && 'answers' in question.data) {
            return question.data.answers;
        } else {
            return [];
        }
    }, [question]);
    const [defaulLogic, setDefaultLogic] = useState<
        IQuestionLogicBranchValues[]
    >([]);
    const currentCampaignId = Number(useParams().id);

    const methods = useForm({
        resolver: yupResolver(branchQuestionSchema),
        defaultValues: {
            logics: defaulLogic,
        },
    });

    const { handleSubmit, control, formState } = methods;

    const { append, fields } = useFieldArray({
        control,
        name: 'logics',
    });

    const dispatch = useAppDispatch();
    const onSubmit = handleSubmit((data) => {
        const saveData = async () => {
            try {
                dispatch(setIsLoading(true));
                const response = await saveBranchLogicOptions(
                    currentCampaignId,
                    Number(question.id),
                    data
                );
                if (response.success) {
                    toast.success(response.message);
                    replaceQuestionByQuestionId(
                        Number(question.id),
                        listCampaignQuestionsFormatter(response.data.question)
                    );
                    dispatch(setIsLoading(false));
                    onClose();
                } else {
                    dispatch(setIsLoading(false));
                    toast.error(response.message);
                }
            } catch (error) {
                dispatch(setIsLoading(false));
                handleError(error);
            }
        };
        saveData();
    });

    const errors1: FieldErrors<{
        logics: IQuestionLogicBranchValues[];
    }> = formState?.errors;

    useEffect(() => {
        const refreshOptions = () => {
            const newDefaultLogic = {
                question: Number(question.id),
                conditions: [
                    {
                        condition: conditionOptions[0]?.value || 1,
                        answer: Number(questionAnswers[0]?.id) || 1,
                    },
                ],
                jumpToQuestion: questionOptions[0]?.value || 1,
            };
            setDefaultLogic([newDefaultLogic]);
            if (values.length) {
                methods.reset({
                    logics: values,
                });
            } else {
                methods.reset({
                    logics: [newDefaultLogic],
                });
            }
        };
        refreshOptions();
    }, [
        questionOptions,
        conditionOptions,
        questionAnswers,
        methods,
        values,
        question.id,
    ]);

    return (
        <>
            <Typography
                variant="h6"
                sx={{
                    color: common.black,
                    fontWeight: '600',
                    fontSize: '30px',
                }}
            >
                Branch Question
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    color: common.black,
                    fontWeight: '400',
                    fontSize: '14px',
                    marginBottom: '16px',
                }}
            >
                Khảo sát rẽ nhánh. Chọn đáp án này thì đi đến câu...
            </Typography>
            <FormProvider methods={methods} onSubmit={onSubmit}>
                {fields.map((item, index) => (
                    <Box
                        p={3}
                        key={index}
                        mb={2}
                        sx={{
                            backgroundColor: 'var(--color-primary-light-26)',
                            border: `2px dashed ${colors.main}`,
                        }}
                    >
                        <Grid
                            container
                            spacing={2}
                            alignItems={'center'}
                            mb={1}
                        >
                            <Grid item md={2}>
                                <Box
                                    sx={{
                                        fontSize: '24px',
                                        fontWeight: 600,
                                    }}
                                >
                                    Nếu
                                </Box>
                            </Grid>
                            <Grid item md={10}>
                                <Grid item md={12}>
                                    <BaseSelectField
                                        name={`logics.${index}.question`}
                                        sx={{
                                            ...styleSelect,
                                            '& .MuiSelect-icon': {
                                                right: '20px',
                                                fontSize: '50px ',
                                            },
                                        }}
                                        disabled={true}
                                    >
                                        <MenuItem
                                            value={question.id?.toString()}
                                        >
                                            Q{question.questionNumber}.{' '}
                                            {question.question}
                                        </MenuItem>
                                    </BaseSelectField>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} mb={1}>
                            <Grid item md={2}></Grid>
                            <Grid item md={10}>
                                <ConditionFieldArray
                                    control={control}
                                    logicIndex={index}
                                    conditionOptions={conditionOptions}
                                    questionAnswers={questionAnswers}
                                    defaulLogic={defaulLogic}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} alignItems={'center'}>
                            <Grid item md={2}>
                                <Box
                                    sx={{
                                        fontSize: '24px',
                                        fontWeight: 600,
                                    }}
                                >
                                    Nhảy đến
                                </Box>
                            </Grid>
                            <Grid item md={10}>
                                <Grid item md={12}>
                                    <BaseSelectField
                                        name={`logics.${index}.jumpToQuestion`}
                                        sx={{
                                            ...styleSelect,
                                            '& .MuiSelect-icon': {
                                                right: '20px',
                                                fontSize: '50px ',
                                            },
                                        }}
                                    >
                                        {questionOptions.map((option) => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </BaseSelectField>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                ))}
                {errors1.logics && (
                    <Typography
                        variant="body2"
                        sx={{ color: 'red', textAlign: 'center' }}
                    >
                        {errors1.logics?.root?.message}
                    </Typography>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 2,
                    }}
                >
                    <Button
                        onClick={() => {
                            append(defaulLogic[0]);
                        }}
                        variant="contained"
                        sx={styles.button}
                        startIcon={<PlusIcon />}
                    >
                        Thêm điều kiện
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            sx={{
                                ...styles.button,
                                minWidth: '155px',
                                borderWidth: '2px',
                            }}
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            onClick={onClose}
                        >
                            Đóng
                        </Button>
                        <Button
                            sx={{
                                ...styles.button,
                                minWidth: '155px',
                            }}
                            type="submit"
                            variant="contained"
                            startIcon={<SaveIcon />}
                        >
                            Lưu
                        </Button>
                    </Box>
                </Box>
            </FormProvider>
        </>
    );
};
const styles = createStyleSheet({
    btnAddLogic: {
        borderRadius: '50px',
        minHeight: '60px',
        marginBottom: '8px',
        width: '100%',
        textTransform: 'none',
        fontSize: '20px',
        backgroundColor: common.white,
        color: '#000000B2',
        boxShadow: 'none',
        ':hover': {
            backgroundColor: common.white,
            boxShadow: 'none',
        },
        border: '1px solid #8B8B8B',
    },
    button: {
        minWidth: '246px',
        minHeight: '60px',
        borderRadius: '40px',
        textTransform: 'none',
        fontSize: '20px',
        fontWeight: 700,
    },
});

export default LogicBranchQuestion;
